import { useState } from 'react';
import { User, Property } from '../../types/user';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { updateUser } from '../../services/users';
import toast from 'react-hot-toast';

interface EditUserFormProps {
  user: User;
  properties: Property[];
  onUserUpdated: () => void;
  onCancel: () => void;
}

interface UpdateUserData {
  id: string;
  name: string;
  propertyId: string | null;
  password?: string;
}

export const EditUserForm = ({ user, properties, onUserUpdated, onCancel }: EditUserFormProps) => {
  const [loading, setLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [formData, setFormData] = useState({
    name: user.name,
    propertyId: user.propertyId || '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (showPasswordFields) {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Las contraseñas no coinciden');
          return;
        }
        if (formData.password.length < 6) {
          toast.error('La contraseña debe tener al menos 6 caracteres');
          return;
        }
      }

      const updateData: UpdateUserData = {
        id: user.id,
        name: formData.name,
        propertyId: formData.propertyId || null
      };

      if (showPasswordFields) {
        updateData.password = formData.password;
      }

      await updateUser(updateData);
      
      toast.success('Usuario actualizado exitosamente');
      onUserUpdated();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      toast.error('Error al actualizar el usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Nombre completo"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Ej: Juan Pérez"
        required
      />

      <Select
        label="Propiedad asignada"
        value={formData.propertyId}
        onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
        options={[
          { value: '', label: 'Sin propiedad asignada' },
          ...properties.map(property => ({
            value: property.id,
            label: property.name
          }))
        ]}
      />

      <div className="border-t pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setShowPasswordFields(!showPasswordFields)}
          className="w-full"
        >
          {showPasswordFields ? 'Cancelar cambio de contraseña' : 'Cambiar contraseña'}
        </Button>

        {showPasswordFields && (
          <div className="space-y-4 mt-4">
            <Input
              type="password"
              label="Nueva contraseña"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mínimo 6 caracteres"
              minLength={6}
              required={showPasswordFields}
            />
            <Input
              type="password"
              label="Confirmar contraseña"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Repite la contraseña"
              minLength={6}
              required={showPasswordFields}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          loading={loading}
        >
          Actualizar Usuario
        </Button>
      </div>
    </form>
  );
};
